import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Share2, X, Download, Copy, Twitter } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const SITE_URL = typeof window !== "undefined" ? window.location.origin : "";

export default function ShareRank({ profile }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [cardUrl, setCardUrl] = useState(null);

  // Share the backend unfurl URL (rich OG meta for X/Discord); it redirects humans to the SPA page.
  const shareUrl = info ? `${API}/u/${encodeURIComponent(info.rsn)}` : SITE_URL;
  const humanUrl = info ? `${SITE_URL}/u/${encodeURIComponent(info.rsn)}` : SITE_URL;
  const caption = info
    ? `I'm rank ${info.rank ? "#" + info.rank : "climbing the ranks"} on TaskScape as ${info.rsn} with ${info.total_points} pts${info.streak ? ` and a ${info.streak}🔥 streak` : ""}! Track your OSRS grind & compete for a free Bond 🏆`
    : "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/profiles/${profile.id}/share`);
      setInfo(data);
      const res = await api.get(`/profiles/${profile.id}/share-card.png`, { responseType: "blob" });
      setCardUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setOpen(true);
    load();
  };

  useEffect(() => {
    const handler = () => openModal();
    window.addEventListener("taskscape:open-share", handler);
    return () => window.removeEventListener("taskscape:open-share", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const close = () => {
    setOpen(false);
    if (cardUrl) URL.revokeObjectURL(cardUrl);
    setCardUrl(null);
    setInfo(null);
  };

  const download = () => {
    if (!cardUrl) return;
    const a = document.createElement("a");
    a.href = cardUrl;
    a.download = `taskscape-${(info?.rsn || "rank").replace(/\s+/g, "_")}.png`;
    a.click();
  };

  const shareX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyForDiscord = async () => {
    try {
      await navigator.clipboard.writeText(`${caption}\n${shareUrl}`);
      toast.success("Caption + link copied! Paste it in Discord — it auto-unfurls your rank card.");
    } catch {
      toast.error("Couldn't copy — long-press the caption to copy it manually.");
    }
  };

  return (
    <>
      <button data-testid="share-rank-btn" onClick={openModal} className="osrs-btn osrs-btn-green px-3 py-2 text-xs flex items-center gap-2">
        <Share2 size={14} /> Share rank
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4" onClick={close}>
          <div className="osrs-panel p-5 w-full max-w-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="share-rank-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="osrs-title text-2xl flex items-center gap-2"><Share2 size={20} /> Share your rank</h3>
              <button onClick={close} className="osrs-btn px-2 py-1" data-testid="close-share"><X size={16} /></button>
            </div>

            <div className="border-2 border-black bg-black/40 mb-4 min-h-[180px] flex items-center justify-center overflow-hidden">
              {loading || !cardUrl ? (
                <p className="osrs-shadow text-[#E6A519] py-16" data-testid="share-loading">Forging your rank card...</p>
              ) : (
                <img src={cardUrl} alt="Your TaskScape rank card" className="w-full block" data-testid="share-card-img" />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-center">
              <button data-testid="share-download-btn" onClick={download} disabled={!cardUrl} className="osrs-btn px-4 py-2 text-sm flex items-center gap-2">
                <Download size={15} /> Download card
              </button>
              <button data-testid="share-x-btn" onClick={shareX} disabled={!info} className="osrs-btn px-4 py-2 text-sm flex items-center gap-2">
                <Twitter size={15} /> Share on X
              </button>
              <button data-testid="share-discord-btn" onClick={copyForDiscord} disabled={!info} className="osrs-btn px-4 py-2 text-sm flex items-center gap-2">
                <Copy size={15} /> Copy for Discord
              </button>
            </div>
            <p className="osrs-shadow text-[11px] text-[#b9a06a] text-center mt-3">
              Sharing the link auto-unfurls your rank card on X &amp; Discord.{" "}
              <a href={humanUrl} target="_blank" rel="noopener noreferrer" className="underline text-[#E6A519] hover:text-[#FFCF3F]" data-testid="view-public-page">
                View public page
              </a>
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
